// src/constants/macro/economyIndicatorsColumns.tsx
/**
 * Column definitions for Economy Indicators tables.
 * Uses the reusable column builders for consistent styling.
 */

import { percentColumn } from "@/components/tables";
import type { SortableColumn } from "@/components/tables";
import type { EconomyIndicatorDisplayRow } from "@/types/dashboards/macro";
import { getCountryFlag } from "@/utils/countryFlags";

/**
 * Country column with flag emoji
 */
const countryColumn: SortableColumn<EconomyIndicatorDisplayRow> = {
  id: "country",
  header: "Country",
  align: "left",
  cell: (row: EconomyIndicatorDisplayRow) => {
    const flag = getCountryFlag(row.country);
    return (
      <span className="text-lg">
        {flag} {row.country}
      </span>
    );
  },
  sortValue: (row: EconomyIndicatorDisplayRow) => row.country,
};

/**
 * Indicator name column
 */
const indicatorColumn: SortableColumn<EconomyIndicatorDisplayRow> = {
  id: "indicator",
  header: "Indicator",
  align: "left",
  cell: (row: EconomyIndicatorDisplayRow) => (
    <span className="text-slate-100 font-medium text-sm">{row.indicator}</span>
  ),
  sortValue: (row: EconomyIndicatorDisplayRow) => row.indicator,
};

/**
 * Current value column
 */
const currentColumn: SortableColumn<EconomyIndicatorDisplayRow> = {
  id: "current",
  header: "Current",
  align: "right",
  cell: (row: EconomyIndicatorDisplayRow) => (
    <span className="tabular-nums text-emerald-400 font-medium">
      {row.current ?? "–"}
    </span>
  ),
  sortValue: (row: EconomyIndicatorDisplayRow) => {
    const val = parseFloat(row.current?.replace(/[^0-9.-]/g, "") ?? "");
    return Number.isFinite(val) ? val : -Infinity;
  },
};

/**
 * Previous/Last value column
 */
const lastColumn: SortableColumn<EconomyIndicatorDisplayRow> = {
  id: "last",
  header: "Previous",
  align: "right",
  cell: (row: EconomyIndicatorDisplayRow) => (
    <span className="tabular-nums text-slate-300">{row.last ?? "–"}</span>
  ),
  sortValue: (row: EconomyIndicatorDisplayRow) => {
    const val = parseFloat(row.last?.replace(/[^0-9.-]/g, "") ?? "");
    return Number.isFinite(val) ? val : -Infinity;
  },
};

/**
 * Change column (absolute)
 */
const changeColumn: SortableColumn<EconomyIndicatorDisplayRow> = {
  id: "change",
  header: "Change",
  align: "right",
  cell: (row: EconomyIndicatorDisplayRow) => {
    const changeStr = row.change;
    if (!changeStr || changeStr === "–" || changeStr === "N/A") {
      return <span className="text-slate-400">–</span>;
    }

    // Parse numeric value for coloring
    const numValue = parseFloat(changeStr.replace(/[^0-9.-]/g, ""));
    const colorClass =
      !Number.isFinite(numValue) || numValue === 0
        ? "text-slate-400"
        : numValue > 0
        ? "text-emerald-400"
        : "text-rose-400";

    return (
      <span className={`tabular-nums font-semibold ${colorClass}`}>
        {changeStr}
      </span>
    );
  },
  sortValue: (row: EconomyIndicatorDisplayRow) => {
    const val = parseFloat(row.change?.replace(/[^0-9.-]/g, "") ?? "");
    return Number.isFinite(val) ? val : -Infinity;
  },
};

/**
 * Forecast column
 */
const forecastColumn: SortableColumn<EconomyIndicatorDisplayRow> = {
  id: "forecast",
  header: "Forecast",
  align: "right",
  cell: (row: EconomyIndicatorDisplayRow) => (
    <span className="tabular-nums text-amber-400 font-medium">
      {row.forecast ?? "–"}
    </span>
  ),
  sortValue: (row: EconomyIndicatorDisplayRow) => {
    const val = parseFloat(row.forecast?.replace(/[^0-9.-]/g, "") ?? "");
    return Number.isFinite(val) ? val : -Infinity;
  },
};

/**
 * Frequency column
 */
const frequencyColumn: SortableColumn<EconomyIndicatorDisplayRow> = {
  id: "frequency",
  header: "Frequency",
  align: "left",
  cell: (row: EconomyIndicatorDisplayRow) => (
    <span className="text-slate-400 text-xs">{row.frequency ?? "–"}</span>
  ),
  sortValue: (row: EconomyIndicatorDisplayRow) => row.frequency ?? "",
};

/**
 * Last updated column
 */
const lastUpdatedColumn: SortableColumn<EconomyIndicatorDisplayRow> = {
  id: "lastUpdated",
  header: "Last Updated",
  align: "right",
  cell: (row: EconomyIndicatorDisplayRow) => (
    <span className="text-slate-400 text-xs">{row.lastUpdated ?? "–"}</span>
  ),
  sortValue: (row: EconomyIndicatorDisplayRow) => row.lastUpdated ?? "",
};

/**
 * Complete column definitions for Economy Indicators table
 */
export const ECONOMY_INDICATORS_COLUMNS: SortableColumn<EconomyIndicatorDisplayRow>[] =
  [
    countryColumn,
    indicatorColumn,
    currentColumn,
    lastColumn,
    changeColumn,
    percentColumn<EconomyIndicatorDisplayRow>({
      id: "percentChange",
      header: "% Change",
      accessor: (r) => r.percentChange,
      alreadyPercent: true,
      decimals: 2,
    }),
    forecastColumn,
    percentColumn<EconomyIndicatorDisplayRow>({
      id: "forecastMonthOnMonth",
      header: "Forecast MoM",
      accessor: (r) => r.forecastMonthOnMonth,
      alreadyPercent: true,
      decimals: 2,
      colorize: false,
    }),
    frequencyColumn,
    lastUpdatedColumn,
  ];
